import { z } from 'zod';
import { EINHEITEN, RECHENARTEN } from './domain/position.js';
import { LEISTUNGEN } from './domain/leistungen.js';
import { AREA_TYPES, DEDUCTION_KINDS, FACADE_TEMPLATES, MEASUREMENT_KINDS, NOTE_TARGETS, ORDER_STATUSES, SURFACE_TYPES, } from './domain/types.js';
/**
 * Validierungsschemata für die REST-Schnittstelle.
 *
 * Der Server validiert damit **jede** Eingabe; der Client nutzt dieselben
 * Schemata, um offline erfasste Daten vor dem Einreihen in die
 * Synchronisationswarteschlange zu prüfen.
 */
// UUIDs werden clientseitig erzeugt (Offline-Anlage), daher hier geprüft.
export const uuidSchema = z.string().uuid('Ungültige ID');
const isoDateSchema = z
    .string()
    .min(1)
    .refine((value) => !Number.isNaN(Date.parse(value)), 'Ungültiger Zeitstempel');
/** Länge/Breite/Höhe in Metern. Grenzen verhindern Tippfehler wie "1000". */
const measureSchema = z.number().finite().min(0).max(500).nullable();
/** Flächen in m². */
const areaValueSchema = z.number().finite().min(0).max(1_000_000).nullable();
const text = (max) => z.string().max(max);
// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------
export const loginSchema = z.object({
    username: z.string().trim().min(1, 'Benutzername erforderlich').max(64),
    password: z.string().min(1, 'Passwort erforderlich').max(256),
});
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1).max(256),
    newPassword: z.string().min(10, 'Mindestens 10 Zeichen').max(256),
});
// ---------------------------------------------------------------------------
// Fachliche Datensätze (vollständige Nutzlast je Datensatz)
// ---------------------------------------------------------------------------
export const orderPayloadSchema = z.object({
    id: uuidSchema,
    customerName: text(200),
    street: text(200),
    zip: text(20),
    city: text(120),
    contactName: text(200),
    phone: text(60),
    notes: text(20000),
    status: z.enum(ORDER_STATUSES),
    createdAt: isoDateSchema,
    updatedAt: isoDateSchema,
});
export const areaPayloadSchema = z.object({
    id: uuidSchema,
    orderId: uuidSchema,
    type: z.enum(AREA_TYPES),
    label: text(200),
    notes: text(20000),
    sortIndex: z.number().int().min(0).max(100000),
    createdAt: isoDateSchema,
    updatedAt: isoDateSchema,
});
export const measurementPayloadSchema = z.object({
    id: uuidSchema,
    areaId: uuidSchema,
    kind: z.enum(MEASUREMENT_KINDS),
    label: text(200),
    surfaceType: z.enum(SURFACE_TYPES).nullable(),
    length: measureSchema,
    width: measureSchema,
    height: measureSchema,
    manualArea: areaValueSchema,
    note: text(5000),
    sortIndex: z.number().int().min(0).max(100000),
    createdAt: isoDateSchema,
    updatedAt: isoDateSchema,
});
export const deductionPayloadSchema = z.object({
    id: uuidSchema,
    measurementId: uuidSchema,
    kind: z.enum(DEDUCTION_KINDS),
    label: text(200),
    width: measureSchema,
    height: measureSchema,
    manualArea: areaValueSchema,
    count: z.number().int().min(1).max(999),
    sortIndex: z.number().int().min(0).max(100000),
    createdAt: isoDateSchema,
    updatedAt: isoDateSchema,
});
export const facadeMeasurementPayloadSchema = z.object({
    id: uuidSchema,
    areaId: uuidSchema,
    template: z.enum(FACADE_TEMPLATES),
    label: text(200),
    width: measureSchema,
    height: measureSchema,
    eavesHeight: measureSchema,
    gableHeight: measureSchema,
    manualArea: areaValueSchema,
    note: text(5000),
    sortIndex: z.number().int().min(0).max(100000),
    createdAt: isoDateSchema,
    updatedAt: isoDateSchema,
});
export const checklistValuePayloadSchema = z.object({
    id: uuidSchema,
    areaId: uuidSchema,
    fieldKey: z
        .string()
        .min(1)
        .max(64)
        .regex(/^[a-z0-9_]+$/, 'Ungültiger Feldschlüssel'),
    value: text(20000),
    createdAt: isoDateSchema,
    updatedAt: isoDateSchema,
});
export const notePayloadSchema = z.object({
    id: uuidSchema,
    orderId: uuidSchema,
    targetType: z.enum(NOTE_TARGETS),
    areaId: uuidSchema.nullable(),
    text: text(20000),
    createdAt: isoDateSchema,
    updatedAt: isoDateSchema,
});
export const positionPayloadSchema = z.object({
    id: uuidSchema,
    orderId: uuidSchema,
    leistung: z.enum(LEISTUNGEN),
    label: text(200),
    menge: areaValueSchema,
    einheit: z.enum(EINHEITEN),
    rechenart: z.enum(RECHENARTEN).nullable(),
    laenge: measureSchema,
    breite: measureSchema,
    hoehe: measureSchema,
    giebelhoehe: measureSchema,
    note: text(20000),
    herkunft: text(500),
    sortIndex: z.number().int().min(0).max(100000),
    createdAt: isoDateSchema,
    updatedAt: isoDateSchema,
});
/**
 * Foto-Metadaten. Die Binärdatei wird getrennt per Multipart hochgeladen –
 * das Schema beschreibt nur den Datenbankdatensatz.
 */
export const photoPayloadSchema = z.object({
    id: uuidSchema,
    orderId: uuidSchema,
    /** `null` = allgemeines Auftragsfoto. */
    positionId: uuidSchema.nullable(),
    originalName: text(255),
    mimeType: z.enum(['image/jpeg', 'image/png', 'image/webp']),
    byteSize: z.number().int().min(0).max(100_000_000),
    width: z.number().int().min(0).max(30000).nullable(),
    height: z.number().int().min(0).max(30000).nullable(),
    note: text(5000),
    createdAt: isoDateSchema,
    updatedAt: isoDateSchema,
});
// ---------------------------------------------------------------------------
// Synchronisation
// ---------------------------------------------------------------------------
/**
 * Synchronisierte Entitäten.
 *
 * Beim Umbau auf Positionen von acht auf vier verkleinert. Die Tabellen
 * `areas`, `measurements`, `deductions`, `facade_measurements` und
 * `checklist_values` bleiben in der Datenbank bestehen, werden aber nicht
 * mehr gelesen oder geschrieben (siehe UMBAU.md).
 */
export const SYNC_ENTITIES = ['orders', 'positions', 'notes', 'photos'];
/** Reihenfolge, in der Mutationen angewendet werden müssen (Fremdschlüssel). */
export const SYNC_ENTITY_ORDER = ['orders', 'positions', 'notes', 'photos'];
export const payloadSchemas = {
    orders: orderPayloadSchema,
    positions: positionPayloadSchema,
    notes: notePayloadSchema,
    photos: photoPayloadSchema,
};
export const syncMutationSchema = z.object({
    /** Eindeutige ID der Mutation – macht das Einspielen idempotent. */
    mutationId: uuidSchema,
    entity: z.enum(SYNC_ENTITIES),
    op: z.enum(['upsert', 'delete']),
    id: uuidSchema,
    /** Version, auf deren Basis der Client geändert hat. 0 = Neuanlage. */
    baseVersion: z.number().int().min(0),
    payload: z.record(z.string(), z.unknown()).nullable(),
    clientUpdatedAt: isoDateSchema,
});
export const syncPushSchema = z.object({
    mutations: z.array(syncMutationSchema).max(500),
});
export const syncPullQuerySchema = z.object({
    since: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(5000).optional(),
});
// ---------------------------------------------------------------------------
// Sonstige Endpunkte
// ---------------------------------------------------------------------------
export const updateOrderStatusSchema = z.object({
    status: z.enum(ORDER_STATUSES),
    version: z.number().int().min(0),
});
export const photoUploadMetaSchema = z.object({
    photoId: uuidSchema,
    orderId: uuidSchema,
    positionId: uuidSchema.nullable().optional(),
    note: text(5000).optional(),
    width: z.coerce.number().int().min(0).max(30000).optional(),
    height: z.coerce.number().int().min(0).max(30000).optional(),
    createdAt: isoDateSchema.optional(),
});
//# sourceMappingURL=schemas.js.map